import React from 'react'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { SectionTitle } from './SchemaSelectionSection'
import {
  CanvasContainer,
  CanvasContent,
  CanvasHeader,
  CanvasItemList,
  ContentPlaceholder,
  BottomDropZone,
} from './DesignerCanvasSection.styles'
import { IReportComponentInstance } from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { getComponentAccent, renderCanvasItem, useDesignerCanvasActions } from './DesignerCanvasSection.action'

export interface IDesignerCanvasSectionProps {
  canvasItems?: IReportComponentInstance[]
  selectedItemId?: string | null
  columns?: IFormSchemaColumn[]
  onSelectItem?: (id: string) => void
  onDrop?: (e: React.DragEvent) => void
  onDropAtIndex?: (e: React.DragEvent, insertIndex: number) => void
  onDragOver?: (e: React.DragEvent) => void
  onReorder?: (fromIndex: number, toIndex: number) => void
  onDeleteItem?: (id: string) => void
}

export const DesignerCanvasSection: React.FC<IDesignerCanvasSectionProps> = ({
  canvasItems = [],
  selectedItemId,
  columns,
  onSelectItem,
  onDrop,
  onDropAtIndex,
  onDragOver,
  onReorder,
  onDeleteItem,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const theme = useTheme()
  const COMPONENT_ACCENT = getComponentAccent(theme)

  const {
    insertIndex,
    draggingIndex,
    isDragActive,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleItemDragStart,
    handleItemDragOver,
    handleItemDrop,
    handleItemDragEnd,
    handleBottomZoneDragOver,
    handleBottomZoneDrop,
  } = useDesignerCanvasActions({ canvasItems, onReorder, onDropAtIndex, onDrop, onDragOver })

  const canvasItemCtx = {
    componentAccent: COMPONENT_ACCENT,
    selectedItemId,
    draggingIndex,
    columns,
    onSelectItem,
    onDeleteItem,
    handleItemDragStart,
    handleItemDragOver,
    handleItemDrop,
    handleItemDragEnd,
  }

  return (
    <CanvasContainer>
      <CanvasHeader>
        <SectionTitle>{translate('canvas-title', { ns: 'dynamic-report' })}</SectionTitle>
      </CanvasHeader>
      <CanvasContent onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
        {canvasItems.length === 0 ? (
          <ContentPlaceholder>{translate('canvas-content', { ns: 'dynamic-report' })}</ContentPlaceholder>
        ) : (
          <CanvasItemList>
            {isDragActive && insertIndex === 0 && (
              <BottomDropZone
                isActive
                sx={{ flex: 'none', minHeight: 36, height: 36, marginTop: 0, pointerEvents: 'none' }}
              />
            )}
            {canvasItems.map((item, index) => {
              const isInsertActive = isDragActive && insertIndex === index + 1 && insertIndex !== canvasItems.length
              return (
                <React.Fragment key={item.id}>
                  {renderCanvasItem(item, index, canvasItemCtx)}
                  {isInsertActive && (
                    <BottomDropZone
                      isActive
                      sx={{ flex: 'none', minHeight: 36, height: 36, marginTop: 0, pointerEvents: 'none' }}
                    >
                      {translate('canvas-drop-here', { ns: 'dynamic-report' })}
                    </BottomDropZone>
                  )}
                </React.Fragment>
              )
            })}
          </CanvasItemList>
        )}
        <BottomDropZone
          isActive={insertIndex === canvasItems.length}
          onDragOver={handleBottomZoneDragOver}
          onDrop={handleBottomZoneDrop}
        >
          {translate('canvas-drop-here', { ns: 'dynamic-report' })}
        </BottomDropZone>
      </CanvasContent>
    </CanvasContainer>
  )
}
